import type { SupportedLocales, LocaleInfo } from '../types/i18n'

const SUPPORTED_LOCALES: SupportedLocales[] = ['en', 'it', 'es']
const DEFAULT_LOCALE: SupportedLocales = 'en'
const LOCALE_STORAGE_KEY = 'app_locale'

export const LOCALE_INFO: Record<SupportedLocales, LocaleInfo> = {
  en: {
    code: 'en',
    name: 'English',
    nativeName: 'English',
    flag: '🇺🇸',
    rtl: false,
  },
  it: {
    code: 'it',
    name: 'Italian',
    nativeName: 'Italiano',
    flag: '🇮🇹',
    rtl: false,
  },
  es: {
    code: 'es',
    name: 'Spanish',
    nativeName: 'Español',
    flag: '🇪🇸',
    rtl: false,
  },
}

/**
 * Detects the user's preferred locale from various sources
 */
export function detectLocale(): SupportedLocales {
  try {
    // 1. Check stored preference
    const stored = localStorage.getItem(LOCALE_STORAGE_KEY) as SupportedLocales
    if (stored && SUPPORTED_LOCALES.includes(stored)) {
      return stored
    }

    // 2. Check browser language
    const browserLang = navigator.language.split('-')[0] as SupportedLocales
    if (SUPPORTED_LOCALES.includes(browserLang)) {
      return browserLang
    }

    // 3. Check browser languages
    for (const lang of navigator.languages) {
      const code = lang.split('-')[0] as SupportedLocales
      if (SUPPORTED_LOCALES.includes(code)) {
        return code
      }
    }
  } catch (error) {
    console.warn('Error detecting locale:', error)
  }

  return DEFAULT_LOCALE
}

/**
 * Saves the locale preference to localStorage
 */
export function saveLocale(locale: SupportedLocales): void {
  try {
    localStorage.setItem(LOCALE_STORAGE_KEY, locale)
  } catch (error) {
    console.warn('Error saving locale:', error)
  }
}

/**
 * Gets the saved locale from localStorage
 */
export function getSavedLocale(): SupportedLocales | null {
  try {
    const saved = localStorage.getItem(LOCALE_STORAGE_KEY) as SupportedLocales
    return SUPPORTED_LOCALES.includes(saved) ? saved : null
  } catch (error) {
    console.warn('Error getting saved locale:', error)
    return null
  }
}

/**
 * Checks if a locale is supported
 */
export function isSupportedLocale(locale: string): locale is SupportedLocales {
  return SUPPORTED_LOCALES.includes(locale as SupportedLocales)
}

/**
 * Gets locale information
 */
export function getLocaleInfo(locale: SupportedLocales): LocaleInfo {
  return LOCALE_INFO[locale]
}

/**
 * Gets all available locales
 */
export function getAvailableLocales(): SupportedLocales[] {
  return [...SUPPORTED_LOCALES]
}

/**
 * Gets all locale information
 */
export function getAllLocaleInfo(): LocaleInfo[] {
  return SUPPORTED_LOCALES.map((code) => LOCALE_INFO[code])
}

/**
 * Sets the document language attribute
 */
export function setDocumentLanguage(locale: SupportedLocales): void {
  try {
    document.documentElement.lang = locale
  } catch (error) {
    console.warn('Error setting document language:', error)
  }
}

/**
 * Sets the document direction based on locale
 */
export function setDocumentDirection(locale: SupportedLocales): void {
  try {
    const localeInfo = getLocaleInfo(locale)
    document.documentElement.dir = localeInfo.rtl ? 'rtl' : 'ltr'
  } catch (error) {
    console.warn('Error setting document direction:', error)
  }
}

/**
 * Maps culture values from API to supported locales
 * Handles common culture formats like 'en-US', 'it-IT', 'es-ES', etc.
 */
export function mapCultureToLocale(culture: string | undefined | null): SupportedLocales {
  if (!culture) {
    return DEFAULT_LOCALE
  }

  try {
    // Extract the language code from culture (e.g., 'en-US' -> 'en')
    const languageCode = culture.toLowerCase().split('-')[0] as SupportedLocales
    
    // Check if the extracted language code is supported
    if (SUPPORTED_LOCALES.includes(languageCode)) {
      return languageCode
    }
  } catch (error) {
    console.warn('Error mapping culture to locale:', error)
  }

  // Return default locale if mapping fails
  return DEFAULT_LOCALE
}
